/**
 * Custom Hook: useUIState
 * Manages UI state for navigation, expansion, and highlighting in the Student Portfolio App
 */

import { useState, useCallback } from 'react';

/**
 * Custom hook for managing UI state
 * @param {Object} params - Parameters object
 * @param {Object} params.userProfile - User profile object
 * @param {Array} params.posts - Array of all posts
 * @param {string} params.selectedStudentUid - Currently selected student UID (for teacher view)
 * @returns {Object} UI state and handlers
 */
export const useUIState = ({ userProfile, posts, selectedStudentUid }) => {
  // Track which skill tags are expanded in summary views
  const [expandedTags, setExpandedTags] = useState(new Set());
  
  // Track which grade sections are expanded per student context (default to 12th grade)
  // Structure: { studentUid: Set of grades }
  const [expandedGradesByStudent, setExpandedGradesByStudent] = useState({});
  
  // Track which posts are expanded per student context (collapsed by default)
  // Structure: { studentUid: Set of postIds }
  const [expandedPostsByStudent, setExpandedPostsByStudent] = useState({});
  
  // Track which post to highlight (for notifications)
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  
  // Get current context key for expansion state (student's own uid or selected student uid)
  const currentContextKey = selectedStudentUid || userProfile?.uid || 'default';

  /**
   * Navigates to a specific post, highlighting and scrolling to it
   * @param {string} postId - The post ID to navigate to
   */
  const navigateToPost = (postId) => {
    // Switch to feed view for students, but keep teacher view mode unchanged
    // Note: This would need to be handled in the parent component via callback
    // since we don't manage studentView in this hook
    
    // Set the post to highlight
    setHighlightedPostId(postId);
    
    // Find the post and expand its grade section and the post itself if needed
    const post = posts.find(p => p.id === postId);
    if (post) {
      const postGrade = post.grade || 'Unknown';
      
      // Expand the grade section in the current context
      setExpandedGradesByStudent(prev => {
        const contextGrades = new Set(prev[currentContextKey] || []);
        contextGrades.add(postGrade);
        return {
          ...prev,
          [currentContextKey]: contextGrades
        };
      });
      
      // Also expand the post itself in the current context
      setExpandedPostsByStudent(prev => {
        const contextPosts = new Set(prev[currentContextKey] || []);
        contextPosts.add(postId);
        return {
          ...prev,
          [currentContextKey]: contextPosts
        };
      });
    }
    
    // Scroll to the post after a short delay to ensure the feed is rendered
    setTimeout(() => {
      const postElement = document.getElementById(`post-${postId}`);
      if (postElement) {
        postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    
    // Clear highlight after 3 seconds
    setTimeout(() => {
      setHighlightedPostId(null);
    }, 3000);
  };

  /**
   * Toggles the expansion state of a skill tag in summary views
   * @param {string} tag - The tag/skill name to toggle
   */
  const toggleTagExpansion = (tag) => {
    const newExpandedTags = new Set(expandedTags);
    if (newExpandedTags.has(tag)) {
      newExpandedTags.delete(tag);
    } else {
      newExpandedTags.add(tag);
    }
    setExpandedTags(newExpandedTags);
  };

  /**
   * Toggles the expansion state of a grade section in feed views (in current context)
   * @param {string} grade - The grade level to toggle
   */
  const toggleGradeExpansion = (grade) => {
    setExpandedGradesByStudent(prev => {
      const contextGrades = new Set(prev[currentContextKey] || ['12']); // Default to 12th grade expanded
      if (contextGrades.has(grade)) {
        contextGrades.delete(grade);
      } else {
        contextGrades.add(grade);
      }
      return {
        ...prev,
        [currentContextKey]: contextGrades
      };
    });
  };

  /**
   * Expands a specific tag (used when navigating from matrix)
   * @param {string} tag - The tag to expand
   */
  const expandTag = (tag) => {
    const newExpandedTags = new Set(expandedTags);
    newExpandedTags.add(tag);
    setExpandedTags(newExpandedTags);
  };

  /**
   * Collapses all skill tags (used when returning to matrix view)
   */
  const collapseAllTags = () => {
    setExpandedTags(new Set());
  };

  /**
   * Resets all UI state to defaults (used when logging out/in)
   */
  const resetUIState = useCallback(() => {
    setExpandedTags(new Set());
    setExpandedGradesByStudent({});
    setExpandedPostsByStudent({});
    setHighlightedPostId(null);
  }, []);

  /**
   * Toggles the expansion state of an individual post in the current context
   * @param {string} postId - The post ID to toggle
   */
  const togglePostExpansion = (postId) => {
    setExpandedPostsByStudent(prev => {
      const contextPosts = new Set(prev[currentContextKey] || []);
      if (contextPosts.has(postId)) {
        contextPosts.delete(postId);
      } else {
        contextPosts.add(postId);
      }
      return {
        ...prev,
        [currentContextKey]: contextPosts
      };
    });
  };

  /**
   * Gets the expanded grades for the current context
   * @returns {Set} Set of expanded grade levels
   */
  const getExpandedGrades = () => {
    return expandedGradesByStudent[currentContextKey] || new Set(['12']); // Default to 12th grade expanded
  };

  /**
   * Gets the expanded posts for the current context
   * @returns {Set} Set of expanded post IDs
   */
  const getExpandedPosts = () => {
    return expandedPostsByStudent[currentContextKey] || new Set();
  };

  return {
    // State
    expandedTags,
    setExpandedTags,
    expandedGrades: getExpandedGrades(),
    expandedPosts: getExpandedPosts(),
    highlightedPostId,
    setHighlightedPostId,
    // Handlers
    navigateToPost,
    toggleTagExpansion,
    toggleGradeExpansion,
    togglePostExpansion,
    expandTag,
    collapseAllTags,
    resetUIState
  };
};

